import React, { useMemo } from 'react';
import { ArrowPathIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useFeed } from './useFeed';
import { EventCard } from './EventCard';
import { MergedReactionCard } from './event-cards/MergedReactionCard';
import { FeedEvent, ReactionPayload } from '../../generated-api';

interface ActivityFeedProps {
  className?: string;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({ className = '' }) => {
  const { events, isLoading, error, hasMore, reactionsMap, loadMore, refresh } = useFeed(20);

  // Merge consecutive REACTION events for the same play
  const processedEvents = useMemo(() => {
    const result: Array<FeedEvent | { type: 'merged_reaction'; reactions: Array<{ payload: ReactionPayload; createdAt: string }> }> = [];
    let currentReactionGroup: Array<{ payload: ReactionPayload; createdAt: string }> | null = null;
    let currentPlayId: string | null = null;

    for (const event of events) {
      if (event.eventType === 'REACTION') {
        const reactionPayload = event.payload as ReactionPayload;
        
        // If this is a reaction for the same play as the current group, add it to the group
        if (currentReactionGroup && currentPlayId === reactionPayload.playId) {
          currentReactionGroup.push({
            payload: reactionPayload,
            createdAt: event.createdAt,
          });
        } else {
          // Save previous group if it exists
          if (currentReactionGroup && currentReactionGroup.length > 0) {
            result.push({
              type: 'merged_reaction',
              reactions: currentReactionGroup,
            });
          }
          
          // Start a new group
          currentReactionGroup = [{
            payload: reactionPayload,
            createdAt: event.createdAt,
          }];
          currentPlayId = reactionPayload.playId;
        }
      } else {
        // Save previous reaction group if it exists
        if (currentReactionGroup && currentReactionGroup.length > 0) {
          result.push({
            type: 'merged_reaction',
            reactions: currentReactionGroup,
          });
          currentReactionGroup = null;
          currentPlayId = null;
        }
        
        // Add the non-reaction event
        result.push(event);
      }
    }

    // Don't forget the last group
    if (currentReactionGroup && currentReactionGroup.length > 0) {
      result.push({
        type: 'merged_reaction',
        reactions: currentReactionGroup,
      });
    }

    return result;
  }, [events]);

  if (error && events.length === 0) {
    return (
      <div className={`bg-surface rounded-lg shadow p-6 ${className}`}>
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={refresh}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-fluid-h2 font-semibold text-heading">Activity Feed</h2>
        <button
          onClick={refresh}
          disabled={isLoading}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowPathIcon className={`h-4 w-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Events List */}
      <div className="space-y-4">
        {processedEvents.map((item, index) => {
          if ('type' in item && item.type === 'merged_reaction') {
            return (
              <MergedReactionCard
                key={`merged-reaction-${index}-${item.reactions[0].payload.playId}`}
                reactions={item.reactions}
              />
            );
          } else {
            const event = item as FeedEvent;
            return (
              <EventCard key={event.eventId} event={event} reactionsMap={reactionsMap} />
            );
          }
        })}

        {/* Loading State */}
        {isLoading && events.length === 0 && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && events.length === 0 && (
          <div className="bg-surface rounded-lg shadow-sm border border-edge p-8 text-center">
            <p className="text-muted">No activity yet. Be the first to record a game!</p>
          </div>
        )}

        {/* Load More Button */}
        {hasMore && events.length > 0 && (
          <div className="flex justify-center pt-4">
            <button
              onClick={loadMore}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 bg-surface border border-edge-input rounded-lg text-sm font-medium text-body hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  Loading...
                </>
              ) : (
                <>
                  <ChevronDownIcon className="h-4 w-4 mr-2" />
                  Load More
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
